import { RootState } from '@/lib/store/store';
import { ProductVariantStateItem } from '@/lib/types/store/product-variants';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ProductVariantTableItemUpdate,
    resetState,
    setDeleting,
    setEditingId,
    setError,
    setLoading,
    setPage,
    setPageSize,
    setProductId,
    setSaving,
    setSelectedKeys,
    setSortDirection,
    setSortField,
    setTotalItems,
    setVariants,
    updateVariant
} from '../features/product-variants/product-variant-table-slice';

/**
 * Hook for interacting with the product variant table in the Redux store
 */
export const useProductVariantTable = () => {
  const dispatch = useDispatch();
  const variantTable = useSelector((state: RootState) => state.productVariantTable);

  // Set product ID
  const setCurrentProductId = useCallback((productId: string | null) => {
    dispatch(setProductId(productId));
  }, [dispatch]);

  // Set all variants
  const setAllVariants = useCallback((variants: ProductVariantStateItem[]) => {
    dispatch(setVariants(variants));
  }, [dispatch]);

  // Update a single variant
  const updateSingleVariant = useCallback((update: ProductVariantTableItemUpdate) => {
    dispatch(updateVariant(update));
  }, [dispatch]);

  // Select variants (select rows in table)
  const selectVariants = useCallback((keys: string[]) => {
    dispatch(setSelectedKeys(keys));
  }, [dispatch]);

  // Set editing ID
  const setEditing = useCallback((id: string | null) => {
    dispatch(setEditingId(id));
  }, [dispatch]);

  // Set loading states
  const setLoadingState = useCallback((isLoading: boolean) => {
    dispatch(setLoading(isLoading));
  }, [dispatch]);

  const setSavingState = useCallback((isSaving: boolean) => {
    dispatch(setSaving(isSaving));
  }, [dispatch]);

  const setDeletingState = useCallback((isDeleting: boolean) => {
    dispatch(setDeleting(isDeleting));
  }, [dispatch]);

  // Pagination
  const setPaginationPage = useCallback((page: number) => {
    dispatch(setPage(page));
  }, [dispatch]);

  const setPaginationPageSize = useCallback((pageSize: number) => {
    dispatch(setPageSize(pageSize));
  }, [dispatch]);

  const setTotalItemCount = useCallback((count: number) => {
    dispatch(setTotalItems(count));
  }, [dispatch]);

  // Sorting
  const setSortingField = useCallback((field: string) => {
    dispatch(setSortField(field));
  }, [dispatch]);

  const setSortingDirection = useCallback((direction: 'asc' | 'desc') => {
    dispatch(setSortDirection(direction));
  }, [dispatch]);

  // Error handling
  const setErrorState = useCallback((errorMessage: string | null) => {
    dispatch(setError(errorMessage));
  }, [dispatch]);

  // Reset
  const reset = useCallback(() => {
    dispatch(resetState());
  }, [dispatch]);

  return {
    // State
    ...variantTable,

    // Actions
    setProductId: setCurrentProductId,
    setVariants: setAllVariants,
    updateVariant: updateSingleVariant,
    selectVariants,
    setEditing,
    setLoading: setLoadingState,
    setSaving: setSavingState,
    setDeleting: setDeletingState,
    setPage: setPaginationPage,
    setPageSize: setPaginationPageSize,
    setTotalItems: setTotalItemCount,
    setSortField: setSortingField,
    setSortDirection: setSortingDirection,
    setError: setErrorState,
    reset,
  };
};
